// Weight-as-predictor view: trend sparkline + growth/sleep insight card + recent rows.
// Opens WeightEntry on "Log new weight". PRD §5.6.

import { useMemo, useState } from "react";
import type { EventRow } from "../lib/events";
import { gramsToLbOz } from "../lib/events";
import type { AgeBand } from "../data/tcbSchedule";
import { WeightEntry } from "./WeightEntry";
import { useT } from "../lib/LocaleContext";
import type { Strings } from "../lib/i18n";

type Props = {
  open: boolean;
  events: EventRow[];
  babyName: string;
  band: AgeBand;
  onLogWeight: (grams: number) => Promise<void> | void;
  onClose: () => void;
};

export function WeightSheet({ open, events, babyName, band, onLogWeight, onClose }: Props) {
  const [showEntry, setShowEntry] = useState(false);
  const t = useT();
  if (!open) return null;

  const weights = events
    .filter((e) => e.type === "weight" && e.weight_grams != null)
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  const last = weights[weights.length - 1] ?? null;
  const prev = weights[weights.length - 2] ?? null;

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.handle} />

        <div style={styles.head}>
          <div>
            <div style={styles.headLabel}>{t.weightSheet.weightLabel}</div>
            <div className="bh-num" style={styles.headBig}>
              {last ? formatLbOz(t, last.weight_grams!) : "—"}
            </div>
            <div style={styles.headSub}>
              {last ? t.weightSheet.loggedRel(formatRel(t, last.at)) : t.weightSheet.noneYet}
              {prev && last && t.weightSheet.wasBefore(formatLbOz(t, prev.weight_grams!), formatRel(t, prev.at))}
            </div>
          </div>
        </div>

        <Sparkline weights={weights} />

        <InsightCard babyName={babyName} band={band} latest={last} />

        {weights.length > 0 && (
          <div style={styles.recent}>
            <div style={styles.recentLabel}>{t.weightSheet.recentLabel}</div>
            {[...weights].reverse().slice(0, 5).map((w) => (
              <div key={w.id} style={styles.recentRow}>
                <span style={styles.recentDate}>{new Date(w.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                <span className="bh-num" style={styles.recentWeight}>
                  {formatLbOz(t, w.weight_grams!)}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={styles.actions}>
          <button style={styles.ghost} onClick={onClose}>
            {t.weightSheet.close}
          </button>
          <button style={styles.primary} onClick={() => setShowEntry(true)}>
            {t.weightSheet.logNew}
          </button>
        </div>
      </div>

      <WeightEntry
        open={showEntry}
        initialLb={last ? gramsToLbOz(last.weight_grams!).lb : 11}
        initialOz={last ? gramsToLbOz(last.weight_grams!).oz : 0}
        onSubmit={async (g) => {
          await onLogWeight(g);
        }}
        onClose={() => setShowEntry(false)}
      />
    </div>
  );
}

function Sparkline({ weights }: { weights: EventRow[] }) {
  const t = useT();
  const sample = useMemo(() => weights.slice(-8).map((w) => w.weight_grams!), [weights]);

  if (sample.length === 0) {
    return (
      <div style={{ ...styles.spark, alignItems: "center", justifyContent: "center", color: "var(--bh-ink-3)", fontSize: 13 }}>
        {t.weightSheet.sparklineEmpty}
      </div>
    );
  }
  const min = Math.min(...sample);
  const max = Math.max(...sample);
  const range = Math.max(1, max - min);
  const w = 100;
  const h = 40;
  const xs = sample.map((_, i) => (sample.length === 1 ? w / 2 : (i / (sample.length - 1)) * w));
  const ys = sample.map((v) => h - ((v - min) / range) * h);
  const path = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const fill = `${path} L${w},${h} L0,${h} Z`;

  return (
    <div style={styles.spark}>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="60" preserveAspectRatio="none">
        <path d={fill} fill="var(--bh-feed-soft)" />
        <path d={path} fill="none" stroke="var(--bh-feed)" strokeWidth="1.4" />
        {xs.map((x, i) => (
          <circle key={i} cx={x} cy={ys[i]} r="1.6" fill="var(--bh-feed)" />
        ))}
      </svg>
    </div>
  );
}

function InsightCard({ babyName, band, latest }: { babyName: string; band: AgeBand; latest: EventRow | null }) {
  const t = useT();
  if (!latest || latest.weight_grams == null) {
    return (
      <div style={styles.insight}>
        {t.weightSheet.insightEmpty(babyName)}
      </div>
    );
  }
  const { lb } = gramsToLbOz(latest.weight_grams);
  let copy: string;
  if (lb < 9) {
    copy = t.weightSheet.insightBuilding(lb, babyName);
  } else if (lb < 13) {
    copy = t.weightSheet.insightOnTrack(lb, babyName, band.nightStretchTargetHours ?? 6);
  } else {
    copy = t.weightSheet.insightConsolidating(lb, babyName);
  }
  return <div style={styles.insight}>{copy}</div>;
}

function formatLbOz(t: Strings, grams: number): string {
  const { lb, oz } = gramsToLbOz(grams);
  return t.weightSheet.lbOz(lb, oz);
}

function formatRel(t: Strings, iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const day = 86_400_000;
  if (ms < day) return t.weightSheet.relToday;
  if (ms < day * 2) return t.weightSheet.relYesterday;
  return t.weightSheet.relDaysAgo(Math.floor(ms / day));
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "oklch(0 0 0 / 0.55)",
    zIndex: 100,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  sheet: {
    background: "var(--bh-surface)",
    width: "100%",
    maxWidth: 520,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    padding: "10px 16px max(28px, env(safe-area-inset-bottom))",
    boxShadow: "0 -20px 60px oklch(0 0 0 / 0.4)",
    maxHeight: "92vh",
    overflowY: "auto",
  },
  handle: {
    width: 40,
    height: 4,
    background: "var(--bh-divider)",
    borderRadius: 2,
    margin: "0 auto 14px",
  },
  head: { padding: "0 4px 14px" },
  headLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
  },
  headBig: {
    fontSize: 34,
    fontWeight: 600,
    color: "var(--bh-ink)",
    marginTop: 4,
    lineHeight: 1,
  },
  headSub: { fontSize: 14, color: "var(--bh-ink-3)", marginTop: 6 },
  spark: {
    background: "var(--bh-surface-2)",
    borderRadius: 16,
    padding: 12,
    marginBottom: 14,
    display: "flex",
  },
  insight: {
    background: "var(--bh-surface-2)",
    borderRadius: 16,
    padding: 14,
    fontSize: 15,
    color: "var(--bh-ink-2)",
    lineHeight: 1.45,
    marginBottom: 14,
  },
  recent: { marginBottom: 14 },
  recentLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 6,
  },
  recentRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 4px",
    borderBottom: "1px solid var(--bh-divider)",
  },
  recentDate: { fontSize: 14, color: "var(--bh-ink-2)" },
  recentWeight: { fontSize: 14, color: "var(--bh-ink)", fontWeight: 600 },
  actions: { display: "flex", gap: 8 },
  primary: {
    flex: 1,
    background: "var(--bh-feed)",
    color: "oklch(0.2 0.04 70)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
  ghost: {
    flex: 1,
    background: "var(--bh-surface-2)",
    color: "var(--bh-ink-2)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
};
